import { memo } from 'react';
import { useTranslation } from 'next-i18next';
import Card from './Card';
import Header from './Header';
import Carousel from '../../common/Carousel';
import ComponentWrapper from '../../common/ContentWrapper';
import type MagazineBannerType from '../../../../types/MagazineBanner';
import analytics from '../../../core/analytics/analytics';
import { PromotionType } from '../../../core/constants';
import Button from '../../common/Button';
import CornerArrow from '../../common/Icons/CornerArrow';
import tailwindConfig from '../../../../tailwind.config';

const tabistMagazineUrl = process.env.NEXT_PUBLIC_TABIST_MAGAZINE_URL;

type Props = {
  magazineBanners: MagazineBannerType[];
};

const MagazineBanner = ({ magazineBanners }: Props) => {
  const { t } = useTranslation('home');

  const maxSlidesToShow = 4;
  const numOfBanners = magazineBanners.length || 0;
  const settings = {
    className: '-mx-2', // remove spaces at the far left/right ends from slider container
    cardSpacing: 'px-2', // add spaces at both of the sides of cards
    infinite: numOfBanners > maxSlidesToShow,
    slidesToShow: 4,
    draggable: false,
    prevBtnStyle: '-left-3 card-carousel-arrow-btn top-[140px]',
    nextBtnStyle: '-right-3 card-carousel-arrow-btn top-[140px]',
    prevArrowStyle: 'stroke-2',
    nextArrowStyle: 'stroke-2',
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.1,
          infinite: false,
          arrows: false,
          draggable: true,
        },
      },
    ],
  };

  const events = {
    onClick: (banner: MagazineBannerType) => {
      analytics.google.ecommerce.track.promotion.view(
        {
          promotion_name: PromotionType.MAGAZINE_BANNER,
          promotion_id: banner?.url || '',
        },
        [
          {
            item_id: banner?.url || '',
            item_name: banner?.image?.altText || '',
          },
        ]
      );
    },
  };

  return (
    <div className="mt-12 bg-gray-50 py-10 sm:mt-16">
      <ComponentWrapper>
        <Header />
        <div className="-mr-2 flex flex-col gap-6 sm:mr-0">
          <Carousel
            settings={settings}
            items={magazineBanners}
            renderItem={(banner, prefetchImages) => (
              <Card
                key={banner.id}
                banner={banner}
                onClick={() => events.onClick(banner)}
                prefetch={prefetchImages}
              />
            )}
          />
          {tabistMagazineUrl && (
            <div className="flex w-full justify-center">
              <Button
                color="white"
                textColor="red"
                border
                className="w-fit px-4"
                to={tabistMagazineUrl}
              >
                <span className="flex items-center gap-1">
                  {t('magazine.link_text')}
                  <CornerArrow size={16} color={tailwindConfig.theme.colors.orange[700]} />
                </span>
              </Button>
            </div>
          )}
        </div>
      </ComponentWrapper>
    </div>
  );
};

export default memo(MagazineBanner);
